import { tacheCreation } from '@admin-pmvb/core'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { useState } from 'react'
import { api, corps, type FicheProjet, lire, requetes } from '../api'
import { Formulaire } from './Formulaire'
import { Icone } from './Icone'
import { LigneTache } from './LigneTache'
import { Vide } from './Section'

// Les tâches d'un projet : ouvertes d'abord, puis terminées. Elles ne sont pas dans les vues
// des tâches, sauf celles qu'on y montre (l'œil). Créer une tâche ici la range dans le projet ;
// une tâche existante se rattache par une recherche.

const OUVERTES = ['a_faire', 'en_cours', 'en_attente_tiers']

export function TachesProjet({ projet }: { projet: FicheProjet }) {
  const client = useQueryClient()
  const [mode, setMode] = useState<'nouvelle' | 'rattacher' | null>(null)
  const [erreur, setErreur] = useState<string | null>(null)
  const ouvertes = projet.taches.filter((t) => OUVERTES.includes(t.statut))
  const closes = projet.taches.filter((t) => !OUVERTES.includes(t.statut))
  const [voirCloses, setVoirCloses] = useState(false)
  const rafraichir = () =>
    Promise.all([
      client.invalidateQueries({ queryKey: ['projets'] }),
      client.invalidateQueries({ queryKey: ['taches'] }),
    ])
  const agir = async (f: () => Promise<unknown>) => {
    setErreur(null)
    try {
      await f()
      await rafraichir()
    } catch (e) {
      setErreur((e as Error).message)
    }
  }
  const actions = (t: FicheProjet['taches'][number]) =>
    projet.ecriture && (
      <>
        <button
          type="button"
          className={t.montreeDansTaches ? 'bouton-icone actif' : 'bouton-icone'}
          title={
            t.montreeDansTaches
              ? 'Montrée dans Tâches : la cacher'
              : 'Cachée de Tâches : l’y montrer aussi'
          }
          aria-label={t.montreeDansTaches ? 'Cacher de Tâches' : 'Montrer dans Tâches'}
          aria-pressed={t.montreeDansTaches}
          onClick={() =>
            agir(() =>
              lire(
                api.projets.taches[':id'].montree.$post({
                  param: { id: t.id },
                  json: { montree: !t.montreeDansTaches },
                }),
              ),
            )
          }
        >
          <Icone nom={t.montreeDansTaches ? 'visibility' : 'visibility_off'} />
        </button>
        <button
          type="button"
          className="bouton-icone"
          title="Retirer du projet (elle revient dans Tâches)"
          aria-label="Retirer du projet"
          onClick={() =>
            agir(() => lire(api.projets.taches[':id'].$delete({ param: { id: t.id } })))
          }
        >
          <Icone nom="link_off" />
        </button>
      </>
    )
  return (
    <div className="panneau-taches">
      {projet.ecriture && (
        <div className="ligne-actions">
          <button
            type="button"
            className="bouton petit"
            onClick={() => setMode(mode === 'nouvelle' ? null : 'nouvelle')}
          >
            <Icone nom="add_task" />
            Nouvelle tâche
          </button>
          <button
            type="button"
            className="bouton-discret petit"
            onClick={() => setMode(mode === 'rattacher' ? null : 'rattacher')}
          >
            <Icone nom="link" />
            Rattacher une tâche
          </button>
        </div>
      )}
      {mode === 'nouvelle' && (
        <Formulaire
          champs={[
            { nom: 'titre', libelle: 'Titre', type: 'texte', requis: true },
            { nom: 'dateDue', libelle: 'Pour le', type: 'date' },
            { nom: 'description', libelle: 'Précisions', type: 'zone', lignes: 2 },
          ]}
          libelleBouton="Créer dans le projet"
          onAnnuler={() => setMode(null)}
          onValider={async (v) => {
            await lire(
              api.taches.$post({ json: corps(tacheCreation, { ...v, projetId: projet.id }) }),
            )
            await rafraichir()
            setMode(null)
          }}
        />
      )}
      {mode === 'rattacher' && (
        <Rattacher projet={projet} fermer={() => setMode(null)} apres={rafraichir} />
      )}
      {erreur && <p className="alerte">{erreur}</p>}
      {projet.taches.length === 0 && <Vide>Aucune tâche dans ce projet.</Vide>}
      <div className="carte-flush liste-taches-projet">
        {ouvertes.map((t) => (
          <LigneTache key={t.id} t={t} vue="toutes" dansProjet actionsEnPlus={actions(t)} />
        ))}
      </div>
      {closes.length > 0 && (
        <>
          <button type="button" className="bouton-texte" onClick={() => setVoirCloses(!voirCloses)}>
            {voirCloses ? 'Masquer' : 'Voir'} les {closes.length} terminée
            {closes.length > 1 ? 's' : ''}
          </button>
          {voirCloses && (
            <div className="carte-flush liste-taches-projet">
              {closes.map((t) => (
                <LigneTache key={t.id} t={t} vue="toutes" dansProjet sansStatut />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  )
}

/** Chercher une tâche existante (ouverte, pas déjà dans ce projet) et la rattacher. */
function Rattacher({
  projet,
  fermer,
  apres,
}: {
  projet: FicheProjet
  fermer: () => void
  apres: () => Promise<unknown>
}) {
  const [q, setQ] = useState('')
  const [erreur, setErreur] = useState<string | null>(null)
  const cherche = q.trim().length >= 2
  const trouvees = useQuery({
    ...requetes.taches({ vue: 'toutes', q: q.trim() }),
    enabled: cherche,
  })
  const candidates = (trouvees.data ?? [])
    .filter((t) => t.projetId !== projet.id && OUVERTES.includes(t.statut))
    .slice(0, 12)
  return (
    <div className="rattacher-tache">
      <div className="champ-recherche">
        <Icone nom="search" />
        <input
          type="search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Chercher une tâche par son titre…"
          aria-label="Chercher une tâche à rattacher"
          autoFocus
        />
        <button type="button" className="bouton-texte" onClick={fermer}>
          Fermer
        </button>
      </div>
      {erreur && <p className="alerte">{erreur}</p>}
      {cherche && trouvees.data && candidates.length === 0 && <Vide>Aucune tâche ouverte.</Vide>}
      {candidates.map((t) => (
        <div key={t.id} className="ligne">
          <div className="ligne-contenu">
            <strong>{t.titre}</strong>
            <span className="discret">
              {[t.entite ?? 'Sans entité', t.projet && `déjà dans ${t.projet}`]
                .filter(Boolean)
                .join(' · ')}
            </span>
          </div>
          <div className="ligne-actions">
            <button
              type="button"
              className="bouton-discret"
              onClick={async () => {
                setErreur(null)
                try {
                  await lire(
                    api.projets[':id'].taches.$post({
                      param: { id: projet.id },
                      json: { tacheId: t.id },
                    }),
                  )
                  await apres()
                } catch (e) {
                  setErreur((e as Error).message)
                }
              }}
            >
              Rattacher
            </button>
          </div>
        </div>
      ))}
    </div>
  )
}

/**
 * Sur la fiche d'une tâche : son projet (lien), la ranger dans un projet de son espace ou l'en
 * retirer, et la montrer (ou non) dans les vues des tâches.
 */
export function ProjetDeTache({
  t,
  ecriture,
}: {
  t: { id: string; projetId: string | null; projet: string | null; montreeDansTaches: boolean }
  ecriture: boolean
}) {
  const client = useQueryClient()
  const [erreur, setErreur] = useState<string | null>(null)
  const possibles = useQuery({ ...requetes.projetsPourTache(t.id), enabled: ecriture })
  const agir = async (f: () => Promise<unknown>) => {
    setErreur(null)
    try {
      await f()
      await Promise.all([
        client.invalidateQueries({ queryKey: ['taches'] }),
        client.invalidateQueries({ queryKey: ['projets'] }),
      ])
    } catch (e) {
      setErreur((e as Error).message)
    }
  }
  return (
    <>
      <div className="projet-de-tache">
        {t.projetId ? (
          <Link to="/projets/$id" params={{ id: t.projetId }}>
            {t.projet}
          </Link>
        ) : (
          <span className="discret">Aucun</span>
        )}
        {ecriture && (
          <select
            value={t.projetId ?? ''}
            aria-label="Ranger dans un projet"
            onChange={(e) => {
              const projetId = e.target.value
              void agir(() =>
                projetId
                  ? lire(
                      api.projets[':id'].taches.$post({
                        param: { id: projetId },
                        json: { tacheId: t.id },
                      }),
                    )
                  : lire(api.projets.taches[':id'].$delete({ param: { id: t.id } })),
              )
            }}
          >
            <option value="">{t.projetId ? 'Retirer du projet' : 'Ranger dans un projet…'}</option>
            {(possibles.data ?? []).map((p) => (
              <option key={p.id} value={p.id}>
                {p.titre}
              </option>
            ))}
          </select>
        )}
      </div>
      {t.projetId && (
        <label className="choix-fil">
          <input
            type="checkbox"
            checked={t.montreeDansTaches}
            disabled={!ecriture}
            onChange={(e) =>
              void agir(() =>
                lire(
                  api.projets.taches[':id'].montree.$post({
                    param: { id: t.id },
                    json: { montree: e.target.checked },
                  }),
                ),
              )
            }
          />
          Montrer aussi dans Tâches
        </label>
      )}
      {erreur && <p className="alerte">{erreur}</p>}
    </>
  )
}
